import React from 'react';
import { Link } from 'react-router-dom';
import { CarSimple, ArrowRight } from '@phosphor-icons/react';
import { SEOHead } from '@/components/SEOHead';
import { ROUTES, DESTINATIONS } from '@/data/seoData';

// Index of every route page, grouped by country, so visitors and search
// engines can reach all of them from one place.
export default function AllRoutesPage() {
  const byCountry = ROUTES.reduce((acc, r) => {
    (acc[r.country] = acc[r.country] || []).push(r);
    return acc;
  }, {});
  const countries = Object.keys(byCountry).sort((a, b) => byCountry[b].length - byCountry[a].length);

  return (
    <div className="min-h-screen bg-[#f8f8f6]">
      <SEOHead
        title="Private Airport Transfer Routes and Prices"
        description={`Fixed-price private transfers on ${ROUTES.length} popular airport routes in Europe and the Middle East, from ski resorts to beach towns and city centres.`}
        canonical="/transfers"
      />

      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <CarSimple size={32} weight="fill" className="text-[#1a3a6e]" />
            <span className="font-['Playfair_Display'] text-xl font-semibold text-slate-900">Planet Transfers</span>
          </Link>
          <Link to="/" className="btn-gold py-2 px-5 text-sm">Search transfers</Link>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 py-12">
        <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl font-semibold text-slate-900 mb-3">
          Airport transfer routes
        </h1>
        <p className="text-slate-600 max-w-2xl mb-10">
          Private, fixed-price transfers with meet &amp; greet and flight tracking. Pick a route to see
          the journey time and book online, or search any address from the homepage.
        </p>

        {countries.map(country => (
          <section key={country} className="mb-10">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">{country}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {byCountry[country].map(r => (
                <Link
                  key={r.slug}
                  to={`/transfer/${r.slug}`}
                  className="flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-lg px-5 py-4 hover:border-[#1a3a6e]/50 hover:shadow-md transition-all"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900">{r.from} → {r.to}</p>
                    <p className="text-sm text-slate-500">{r.distance} · {r.duration}</p>
                  </div>
                  <span className="flex items-center gap-1 text-sm font-semibold text-[#1a3a6e] whitespace-nowrap">
                    {r.priceFrom ? `from €${r.priceFrom}` : 'See price'}
                    <ArrowRight size={14} />
                  </span>
                </Link>
              ))}
            </div>
          </section>
        ))}

        <section className="mb-4">
          <h2 className="text-xl font-semibold text-slate-900 mb-4">Airport guides</h2>
          <div className="flex flex-wrap gap-3">
            {DESTINATIONS.map(d => (
              <Link
                key={d.slug}
                to={`/airport-transfer/${d.slug}`}
                className="bg-white border border-slate-200 rounded-lg px-4 py-2 text-sm text-slate-700 hover:border-[#1a3a6e]/50"
              >
                {d.city} airport transfers
              </Link>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
